/**
 * MyAd — Premium Advertiser self-service dashboard.
 *
 * Available to any user with `is_premium_advertiser=true`. Lets them:
 *   • Set a title
 *   • Compose rich-text (react-quill-new + bleach on the server)
 *   • Add / reorder / remove up to 8 images (auto-scroll carousel on Browse)
 *   • Set a "Click Here" link (whole card becomes clickable)
 *   • Toggle their ad active / hidden
 *   • Preview exactly how the ad will appear in the sidebar
 *
 * When the current user isn't flagged as a premium advertiser, we render a
 * quiet explainer instead of the editor.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { CaretLeft, CaretRight, Trash, UploadSimple } from "@phosphor-icons/react";

import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sanitizeAdHtml } from "@/components/SponsorsSidebar";

const MAX_IMAGES = 8;

const QUILL_MODULES = {
  toolbar: [
    [{ header: [3, 4, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};
const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike", "color", "background",
  "list", "link",
];

function emptyDraft() {
  return {
    title: "",
    content: "",
    link_url: "",
    link_new_tab: false,
    is_active: true,
    images: [],
  };
}

/**
 * A single image tile in the "manage images" row. Supports drag-to-reorder
 * via HTML5 native DnD (no extra library — we already ship @dnd-kit but
 * this row is short and predictable so vanilla DnD is enough).
 */
function ImageTile({ src, index, count, onRemove, onMove }) {
  return (
    <div
      className="relative w-24 h-24 shrink-0 border border-white/10 bg-black overflow-hidden group"
      data-testid={`ad-image-${index}`}
    >
      <img src={src} alt={`ad ${index + 1}`} className="w-full h-full object-cover" />
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 flex flex-col items-center justify-center gap-1">
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => onMove(index, index - 1)}
            disabled={index === 0}
            data-testid={`ad-image-left-${index}`}
            aria-label="Move left"
            className="w-6 h-6 flex items-center justify-center bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white"
          >
            <CaretLeft size={12} weight="bold" />
          </button>
          <button
            type="button"
            onClick={() => onMove(index, index + 1)}
            disabled={index === count - 1}
            data-testid={`ad-image-right-${index}`}
            aria-label="Move right"
            className="w-6 h-6 flex items-center justify-center bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white"
          >
            <CaretRight size={12} weight="bold" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => onRemove(index)}
          data-testid={`ad-image-remove-${index}`}
          className="text-red-400 hover:text-red-300 text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
        >
          <Trash size={10} weight="bold" /> remove
        </button>
      </div>
      <div className="absolute top-0.5 left-0.5 font-mono text-[8px] bg-black/70 text-white px-1">
        {index + 1}
      </div>
    </div>
  );
}

function AdPreview({ draft, ownerUsername, slot }) {
  const html = useMemo(() => sanitizeAdHtml(draft.content), [draft.content]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (draft.images.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % draft.images.length), 8000);
    return () => clearInterval(t);
  }, [draft.images.length]);
  useEffect(() => { setIdx(0); }, [draft.images.length]);

  return (
    <div className="border border-white/10 bg-[#0f0f0f] w-full max-w-[240px]">
      <div className="relative">
        {draft.images.length > 0 && (
          <div className="relative w-full bg-black overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
            {draft.images.map((src, i) => (
              <img
                key={src + i} src={src} alt="preview"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500"
                style={{ opacity: i === idx ? 1 : 0 }}
              />
            ))}
          </div>
        )}
        <div className="absolute top-1.5 left-1.5 font-mono text-[8px] tracking-widest uppercase bg-gradient-to-r from-[#FF5F1F] to-[#FFD700] text-black font-black px-1.5 py-0.5">
          Premium
        </div>
        <div className="absolute top-1.5 right-1.5 font-mono text-[8px] bg-black/70 text-white px-1.5 py-0.5">
          Slot {slot}
        </div>
      </div>
      <div className={`p-3 ${draft.images.length === 0 ? "pt-7" : ""}`}>
        {draft.title && <div className="font-display font-black text-sm text-white truncate mb-1">{draft.title}</div>}
        {ownerUsername && <div className="font-mono text-[9px] text-zinc-500 mb-1.5">@{ownerUsername}</div>}
        {html && (
          <div
            className="h57-splash-body text-xs text-zinc-300 leading-snug"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        )}
        {draft.link_url && (
          <div className="mt-2 font-mono text-[10px] text-[#FFD700] tracking-widest uppercase">
            Click Here →
          </div>
        )}
      </div>
    </div>
  );
}

export default function MyAd() {
  return (
    <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 md:py-8">
      <MyAdPanel />
    </div>
  );
}

function MyAdPanel() {
  const { user } = useAuth();
  const [draft, setDraft] = useState(emptyDraft());
  const [slot, setSlot] = useState(null);
  const [isPremium, setIsPremium] = useState(!!user?.is_premium_advertiser);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/my-ad");
      setIsPremium(!!data.is_premium_advertiser);
      setSlot(data.slot || null);
      if (data.ad) {
        setDraft({
          title: data.ad.title || "",
          content: data.ad.content || "",
          link_url: data.ad.link_url || "",
          link_new_tab: !!data.ad.link_new_tab,
          is_active: !!data.ad.is_active,
          images: data.ad.images || [],
        });
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = MAX_IMAGES - draft.images.length;
    if (room <= 0) { toast.error(`Max ${MAX_IMAGES} images per ad.`); return; }
    const chosen = files.slice(0, room);
    if (files.length > room) toast(`Only uploading ${room} of ${files.length} (max reached).`);

    setUploading(true);
    try {
      const uploaded = [];
      for (const f of chosen) {
        const form = new FormData();
        form.append("file", f);
        const { data } = await api.post("/uploads/image", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.url);
      }
      setDraft((d) => ({ ...d, images: [...d.images, ...uploaded] }));
      toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} added`);
    } catch (err) { toast.error(formatErr(err)); }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const moveImage = (from, to) => {
    if (to < 0 || to >= draft.images.length) return;
    setDraft((d) => {
      const arr = [...d.images];
      const [x] = arr.splice(from, 1);
      arr.splice(to, 0, x);
      return { ...d, images: arr };
    });
  };
  const removeImage = (i) => {
    setDraft((d) => ({ ...d, images: d.images.filter((_, idx) => idx !== i) }));
  };

  const save = async () => {
    if ((draft.title || "").length > 120) { toast.error("Title too long (120 max)"); return; }
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        content: draft.content,
        link_url: draft.link_url.trim() || null,
        link_new_tab: !!draft.link_new_tab,
        is_active: !!draft.is_active,
        images: draft.images,
      };
      await api.put("/my-ad", payload);
      toast.success("Ad saved — live on Browse");
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  // ---------- render ----------

  if (loading) return <div className="text-sm text-zinc-500">Loading…</div>;

  if (!isPremium) {
    return (
      <div className="h57-surface p-6 space-y-2" data-testid="myad-not-premium">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Premium Advertising</div>
        <p className="text-sm text-zinc-400 max-w-lg">
          You are not currently a premium advertiser. Premium slots on the Hunt57 Browse page
          are limited to <span className="text-white font-semibold">4 total</span>.
          Contact an administrator if you&apos;re interested in claiming one.
        </p>
      </div>
    );
  }

  if (!slot) {
    return (
      <div className="h57-surface p-6 space-y-2" data-testid="myad-no-slot">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Premium Advertiser</div>
        <p className="text-sm text-zinc-400 max-w-lg">
          You&apos;re flagged as a premium advertiser, but haven&apos;t been assigned a slot yet.
          An administrator needs to assign you a slot (1-4) before your ad can appear on Browse.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="myad-panel">
      <div className="h57-surface p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">
              → My Premium Ad · Slot {slot}
            </div>
            <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
              This ad appears in the Featured Sponsors rail on the Browse page. Images auto-scroll
              every 8 seconds. If you set a link, the entire card becomes clickable.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-zinc-300 select-none">
            <input
              type="checkbox"
              checked={draft.is_active}
              onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
              data-testid="myad-active"
            />
            Active
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-5">
        <div className="space-y-5">
          <div className="h57-surface p-4 space-y-4">
            <div>
              <label className="h57-label">Title (optional)</label>
              <input
                className="h57-input"
                value={draft.title}
                maxLength={120}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder={"Shown in bold above your message"}
                data-testid="myad-title-input"
              />
            </div>

            <div>
              <label className="h57-label">Message</label>
              <div className="h57-quill">
                <ReactQuill
                  theme="snow"
                  value={draft.content}
                  onChange={(html) => setDraft({ ...draft, content: html })}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Write your promo copy…"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className="h57-label">&ldquo;Click Here&rdquo; Link</label>
                <input
                  className="h57-input"
                  value={draft.link_url}
                  maxLength={500}
                  onChange={(e) => setDraft({ ...draft, link_url: e.target.value })}
                  placeholder="https://yourstore.com  or  /listing/…  or  mailto:…"
                  data-testid="myad-link-input"
                />
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-zinc-300 select-none pb-2">
                <input
                  type="checkbox"
                  checked={draft.link_new_tab}
                  onChange={(e) => setDraft({ ...draft, link_new_tab: e.target.checked })}
                  data-testid="myad-newtab"
                />
                New tab
              </label>
            </div>
          </div>

          <div className="h57-surface p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF5F1F]">→ Images</div>
                <div className="font-mono text-[10px] text-zinc-500 mt-1">
                  {draft.images.length} / {MAX_IMAGES} · auto-scrolls every 8s
                </div>
              </div>
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFilesPicked}
                  data-testid="myad-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading || draft.images.length >= MAX_IMAGES}
                  data-testid="myad-add-image"
                  className="h57-btn-primary !py-2 !px-3 text-xs disabled:opacity-40"
                >
                  <UploadSimple size={14} weight="bold" className="inline mr-1" />
                  {uploading ? "Uploading…" : "Add Images"}
                </button>
              </div>
            </div>

            {draft.images.length === 0 ? (
              <div className="border border-dashed border-white/10 p-6 text-center font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                No images yet — add at least one for best results
              </div>
            ) : (
              <div className="flex gap-2 overflow-x-auto pb-2" data-testid="myad-image-list">
                {draft.images.map((src, i) => (
                  <ImageTile
                    key={src + i}
                    src={src}
                    index={i}
                    count={draft.images.length}
                    onMove={moveImage}
                    onRemove={removeImage}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={save}
              disabled={saving || uploading}
              data-testid="myad-save"
              className="h57-btn-primary !py-2 !px-4 text-sm disabled:opacity-40"
            >
              {saving ? "Saving…" : "Save Ad"}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">→ Live Preview</div>
          <AdPreview draft={draft} ownerUsername={user?.username} slot={slot} />
        </div>
      </div>
    </div>
  );
}
